// Desktop (Tauri) bridge — every function feature-detects and no-ops in the
// browser, so this is safe to call unconditionally from shared code.

const tauri = () => (typeof window !== 'undefined' ? window.__TAURI__ : undefined);

export const isDesktopApp = () => !!tauri();

// Dock (macOS) / taskbar badge. Pass 0 to clear.
export const desktopSetBadge = async (count) => {
  try {
    const T = tauri();
    const win = T?.window?.getCurrentWindow?.();
    if (!win?.setBadgeCount) return;
    await win.setBadgeCount(count > 0 ? count : undefined);
  } catch { /* not available on this platform/version */ }
};

// Native OS notification banner.
export const desktopNotify = async (title, body) => {
  try {
    const T = tauri();
    const n = T?.notification;
    if (!n?.sendNotification) return;
    let granted = await n.isPermissionGranted();
    if (!granted) granted = (await n.requestPermission()) === 'granted';
    if (granted) n.sendNotification({ title, body });
  } catch { /* not available */ }
};

// Native banner with click → deep-link (desktop shell v1.0.2+).
// Falls back to the plain plugin banner on older shells that lack the
// notify_native command.
export const desktopNotifyDeep = async (title, body, deepLink) => {
  try {
    const T = tauri();
    if (!T) return;
    if (T.core?.invoke) {
      try {
        await T.core.invoke('notify_native', { title, body, deepLink: deepLink || null });
        return;
      } catch { /* v1.0.1 shell — command not registered */ }
    }
    await desktopNotify(title, body);
  } catch { /* not available */ }
};

// Subscribe to native notification banner clicks (payload = deep-link JSON
// string set at notify time). Returns an unsubscribe fn. No-op in browser.
export const onDesktopNotificationClick = (cb) => {
  const T = tauri();
  if (!T?.event?.listen) return () => {};
  let unlisten = null;
  let disposed = false;
  T.event.listen('notification-clicked', (e) => cb(e.payload))
    .then((u) => { if (disposed) u(); else unlisten = u; })
    .catch(() => {});
  return () => { disposed = true; if (unlisten) unlisten(); };
};

// Collect a notification click that happened before this page was ready
// (cold-launch: banner clicked while the app was closed). One-shot; the shell
// only returns links fresher than 30s. Null in browser / old shells.
export const takePendingDesktopDeepLink = async () => {
  try {
    const T = tauri();
    if (!T?.core?.invoke) return null;
    return await T.core.invoke('take_pending_deep_link');
  } catch { return null; }
};

// 'granted' | 'denied' | null (browser / old shells / query failed).
// 'denied' also covers macOS "not determined" — both mean banners won't show.
export const getDesktopNotificationPermission = async () => {
  try {
    const T = tauri();
    if (!T?.core?.invoke) return null;
    return await T.core.invoke('notification_permission_state');
  } catch { return null; }
};

// Notification types that interrupt with a banner (mirrors mobile push).
// Everything else is badge-only.
export const DESKTOP_BANNER_TYPES = new Set([
  'dm',
  'message_reaction',
  'mention',
  'post',
  'comment',
  'comment_reaction',
  'workshop',
  'planner_review',
  'service_request', 'service_response', 'service_updated', 'service_comment',
]);
